import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import {
  DEFAULT_LOCALE,
  getBrowserLanguageTag,
  resolveSupportedLocale,
} from "utility/locale";
import { DEFAULT_NAMESPACE, NAMESPACES, resources } from "./resources";

/**
 * Whether UI localisation is switched on for this build. When false the app
 * is English-only end to end: init pins `en-GB` and `LocaleSync` is inert, so
 * neither the browser locale nor the learner's preference changes any copy.
 *
 * Read straight from `process.env` (CRA inlines `REACT_APP_*` at build time)
 * rather than through `Constant`, so this module carries no dependency on the
 * app config — keeping it free of the `Constant` mock in unrelated test suites.
 */
export const I18N_ENABLED: boolean =
  process.env.REACT_APP_I18N_ENABLED === "true";

/**
 * The locale the app boots in, before the signed-in learner's preference is
 * known. When localisation is on, derive it from the browser locale so an
 * anonymous French-browser user sees French on the auth screens; `LocaleSync`
 * later reconciles this with `learner.language` once a profile loads. When
 * off, pin `en-GB`.
 */
const initialLocale = I18N_ENABLED
  ? resolveSupportedLocale(getBrowserLanguageTag()) ?? DEFAULT_LOCALE
  : DEFAULT_LOCALE;

i18n.use(initReactI18next).init({
  resources,
  lng: initialLocale,
  fallbackLng: DEFAULT_LOCALE,
  ns: NAMESPACES,
  defaultNS: DEFAULT_NAMESPACE,
  // React already escapes rendered values, so i18next's own HTML escaping
  // would double-encode interpolated text. Disable it.
  interpolation: { escapeValue: false },
  returnNull: false,
  // MT fills locales incrementally; an untranslated ("") value must fall back
  // to en-GB rather than render as an empty string.
  returnEmptyString: false,
});

export default i18n;
